import React from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Award, Building2, ExternalLink, CheckCircle, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const EmpireGroupPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const EMPIRE_IMG = `${API.replace('/api', '')}/api/uploads/empire`;

  const subsidiaries = [
    {
      name: "Empire",
      description: "The real estate division of Empire Group, bringing together industry professionals with over 20 years of experience. Specializing in luxury high-rise condominiums, property transactions, rentals, and comprehensive property management solutions.",
      project: "Empire Tower",
      units: "440 units",
      achievement: "International award-winning 25-floor luxury condominium. Sold out and fully transferred despite launching during COVID-19 pandemic.",
      images: [
        `${EMPIRE_IMG}/subsidiaries-empire.webp`,
        `${EMPIRE_IMG}/empire-tower-04.webp`,
        `${EMPIRE_IMG}/empire-tower-03.webp`
      ]
    },
    {
      name: "Embassy",
      description: "A distinguished brand conceptualized as a central hub for international buyers seeking luxurious holiday homes in Pattaya. Features cutting-edge technology, AI integration, and wellness-focused amenities designed for residents of all ages.",
      project: "Embassy & Embassy Life",
      units: "450 units (Embassy) + 964 units (Embassy Life)",
      achievement: "Best Condominium Development in Asia. 80% sold within 7 months of launch. Embassy Life sold 160 units in first 60 days.",
      images: [
        `${EMPIRE_IMG}/embassy-exterior.webp`,
        `${EMPIRE_IMG}/embassy-lobby.webp`,
        `${EMPIRE_IMG}/embassy-pool.webp`
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-md border-b border-[#D4AF37]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-[#D4AF37] tracking-wide">
                NOK | Thailand Property & Investment
              </h1>
              <p className="text-[#C9A961] text-sm mt-1 font-medium">Authorized Agent - Empire Group</p>
            </div>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back_to_home')}
            </Button>
          </div>
        </div>
      </header>

      {/* Affiliation Badge */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#C9A961]/20 backdrop-blur-lg rounded-2xl border-2 border-[#D4AF37] p-8 mb-12">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="bg-[#131A38] p-6 rounded-xl border border-white/5">
              <img 
                src={`${EMPIRE_IMG}/empire-logo.png`} 
                alt="Empire Group Logo" 
                className="h-16 w-auto"
                style={{ filter: 'invert(1)' }}
              />
            </div>
            <div className="text-center">
              <div className="flex items-center gap-3 justify-center mb-2">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <h2 className="font-display text-3xl font-bold text-[#D4AF37]">
                  Authorized Agent Partnership
                </h2>
              </div>
              <p className="text-gray-300 text-lg">
                NOK Thailand Property & Investment is an official authorized agent for Empire Group developments
              </p>
            </div>
            <div className="bg-[#131A38] p-4 rounded-xl border border-white/5">
              <Award className="w-12 h-12 text-[#D4AF37]" />
            </div>
          </div>
        </div>

        {/* About Empire Group */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">
              About Empire Group
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Empire Group is committed to meeting the evolving demands of the market and customers. 
              With a portfolio of distinguished brands, each with its own unique identity and design philosophy, 
              Empire Group creates exceptional living experiences embodying comfort, elegance, and a refined sense of home.
            </p>
            <div className="h-1 w-32 mx-auto bg-gradient-to-r from-[#D4AF37] to-[#C9A961] rounded-full mt-8"></div>
          </div>

          <div className="mb-12">
            <img 
              src={`${EMPIRE_IMG}/subsidiaries-banner.webp`} 
              alt="Empire Group" 
              className="w-full h-96 object-cover rounded-2xl shadow-2xl border-2 border-[#D4AF37]/30"
            />
          </div>
        </div>

        {/* Subsidiaries */}
        <div className="space-y-16">
          {subsidiaries.map((subsidiary, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-black/70 to-[#1a1f3a]/70 backdrop-blur-lg rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl"
            >
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] p-6">
                <h3 className="font-display text-4xl font-bold text-black">{subsidiary.name}</h3>
              </div>

              <div className="p-8">
                {/* Image Gallery */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {subsidiary.images.map((img, idx) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`${subsidiary.name} ${idx + 1}`}
                      className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
                    />
                  ))}
                </div>

                {/* Description */}
                <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                  {subsidiary.description}
                </p>

                {/* Project Details */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-6">
                    <h4 className="text-[#D4AF37] font-semibold text-xl mb-3 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Featured Project
                    </h4>
                    <p className="text-gray-200 text-lg font-bold mb-2">{subsidiary.project}</p>
                    <p className="text-gray-400">{subsidiary.units}</p>
                  </div>

                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-6">
                    <h4 className="text-[#D4AF37] font-semibold text-xl mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Achievement
                    </h4>
                    <p className="text-gray-300 leading-relaxed">{subsidiary.achievement}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 bg-gradient-to-br from-[#D4AF37]/8 via-[#131A38] to-[#D4AF37]/8 backdrop-blur-lg rounded-2xl border border-[#D4AF37]/20 p-12 text-center">
          <h3 className="font-display text-4xl font-bold text-[#D4AF37] mb-4">
            {t('interested_empire')}
          </h3>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('empire_cta_desc')}
          </p>

          {/* Primary CTA */}
          <Button
            onClick={() => navigate('/team')}
            className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] hover:from-[#C9A961] hover:to-[#D4AF37] text-black font-bold text-lg px-10 py-6 rounded-full mb-8"
          >
            {t('contact_our_team')}
          </Button>

          {/* LINE Contacts */}
          <div className="flex justify-center gap-4 flex-wrap mb-8">
            <a
              href="https://lin.ee/soB6jcI"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#06C755] hover:bg-[#05b34d] text-white font-semibold text-sm rounded-full transition-all duration-300"
              data-testid="empire-line-realestate"
            >
              <MessageCircle className="w-4 h-4" />
              LINE — Legal Advice / Real Estate
              <ExternalLink className="w-3.5 h-3.5 opacity-60" />
            </a>
            <a
              href="https://page.line.me/296nejkj"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#06C755] hover:bg-[#05b34d] text-white font-semibold text-sm rounded-full transition-all duration-300"
              data-testid="empire-line-pook"
            >
              <MessageCircle className="w-4 h-4" />
              LINE — Insurance / Financial Planning
              <ExternalLink className="w-3.5 h-3.5 opacity-60" />
            </a>
          </div>

          {/* Website link */}
          <a
            href="https://empiregroupth.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E2C364] text-sm font-medium transition-colors duration-300"
          >
            {t('visit_empire')}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Official Contact Info */}
        <div className="mt-12 text-center text-gray-400 text-sm">
          <p className="mb-2">Empire Group Official Contact:</p>
          <p>Email: <a href="mailto:info@embassypattaya.com" className="text-[#D4AF37] hover:underline">info@embassypattaya.com</a></p>
          <p>Address: Embassy Condominium Pattaya, 13/84 Moo.12 Nong Prue, Bang Lamung, Chonburi 20150 Thailand</p>
        </div>
      </div>
    </div>
  );
};

export default EmpireGroupPage;
